import { runInAction } from 'mobx';
import langstore from 'System/i18n/translator.js';
import {urlBase64ToUint8Array, swReady, getNotificationSubscriptions} from 'Application/utils/pushNotificationsHelpers';

const PUSH_BANNER_NEVER_ASK_KEY = 'pushNotificationBannerNeverAsk';

/**
 * Flusso condiviso di sottoscrizione: recupera la VAPID key, sottoscrive il PushManager,
 * registra la subscription sul BE. Il BE mantiene sempre l'ultimo endpoint valido:
 * oldEndpoint gli permette di sostituire la riga precedente invece di crearne una nuova.
 * Nessuna UI: gli errori vanno gestiti dal chiamante.
 */
async function performSubscription(store, { oldEndpoint = null } = {})
{
    const vapidRes = await store.dataLayer({
        url: store.config.paths.apiURL + 'push/vapid-public-key',
        cacheAge: null,
        params: {},
        userToken: store.userToken
    });

    if (vapidRes?.status !== 'OK') throw new Error('VAPID key non disponibile');

    const { publicKey } = vapidRes.data;

    const registration = await swReady();
    const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
    });

    const sub = subscription.toJSON();
    const isIos     = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isAndroid = /android/i.test(navigator.userAgent);

    const res = await store.dataLayer({
        url: store.config.paths.apiURL + 'push/subscribe',
        cacheAge: null,
        params: {
            endpoint:    sub.endpoint,
            keys:        sub.keys,
            deviceType:  isIos ? 'ios' : isAndroid ? 'android' : 'desktop',
            userAgent:   navigator.userAgent,
            oldEndpoint: oldEndpoint,
        },
        userToken: store.userToken
    });

    if (res?.status !== 'OK') throw new Error('Errore salvataggio subscription');

    return subscription;
}

/**
 * Verifica supporto browser e stato subscription esistente.
 * Chiamata da main.jsx dopo init(), solo se l'utente è autenticato.
 */
export const pushNotificationInit = async function ()
{
    if (!this.config?.pushNotifications?.enabled) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return;

    runInAction(() => {
        this.pushNotificationSupported = true;
        this.pushNotificationPermission = Notification.permission;
    });

    try {
        const neverAsk = localStorage.getItem(PUSH_BANNER_NEVER_ASK_KEY) === '1';
        const [subscription, backendOk] = await Promise.all([
            getNotificationSubscriptions(),
            this.pushPreferencesLoad({ silent: true, skipGuard: true }),
        ]);
        if (backendOk === true) {
            // Backend è fonte di verità: subscription attiva indipendentemente dallo stato locale.
            runInAction(() => {
                this.pushNotificationSubscribed = true;
                this.pushNotificationSetupPending = false;
            });
            // Riconciliazione: il BE deve avere sempre l'ultimo endpoint valido di questo device.
            // Se la subscription locale manca o non è tra quelle attive sul BE (token ruotato o
            // invalidato dal push service), si rinnova in silenzio: il permesso è già granted,
            // quindi subscribe() non mostra alcun prompt all'utente.
            if (Notification.permission === 'granted') {
                const known = subscription &&
                    (this.pushActiveEndpoints === null || this.pushActiveEndpoints.includes(subscription.endpoint));
                if (!known) {
                    const oldEndpoint = subscription?.endpoint || null;
                    // Una subscription locale non riconosciuta dal BE è stale:
                    // si cancella per forzare la generazione di un endpoint nuovo.
                    if (subscription) await subscription.unsubscribe().catch(() => {});
                    await performSubscription(this, { oldEndpoint }).catch(() => {});
                }
            }
        } else if (backendOk === false) {
            // Backend non conosce la subscription: se locale esiste è orfana → pulizia.
            if (subscription) await subscription.unsubscribe();
            runInAction(() => {
                this.pushNotificationSubscribed = false;
                this.pushNotificationSetupPending = !neverAsk && Notification.permission !== 'denied';
            });
        }
        // backendOk === null = errore auth/rete → stato indeterminato, si lascia il default (false).
    } catch (_) {
        // Service worker non attivo (es. ambiente di sviluppo senza SW)
    }
};

/**
 * Chiude il banner di offerta solo per la sessione corrente. Riapparirà al prossimo reload.
 */
export const pushNotificationBannerDismiss = function ()
{
    runInAction(() => {
        this.pushNotificationSetupPending = false;
    });
};

/**
 * Chiude il banner e lo silenzia in modo permanente (persistito in localStorage).
 */
export const pushNotificationBannerNeverAsk = function ()
{
    try { localStorage.setItem(PUSH_BANNER_NEVER_ASK_KEY, '1'); } catch (_) {}
    runInAction(() => {
        this.pushNotificationSetupPending = false;
    });
};

/**
 * Richiede il permesso, sottoscrive il push manager, invia la subscription al BE.
 */
export const pushNotificationSubscribe = async function ()
{
    if (this.pushNotificationLoading) return;

    runInAction(() => { this.pushNotificationLoading = true; });

    try {
        // 1. Su iOS le push funzionano solo da PWA installata (non da Safari browser)
        const isIosSafari = /iphone|ipad|ipod/i.test(navigator.userAgent);
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone;
        if (isIosSafari && !isStandalone) {
            this.notificationAdd(
                langstore.t('push_ios_not_pwa', 'Per attivare le notifiche su iPhone, aggiungi questa app alla schermata Home e aprila da lì.'),
                'push_ios_not_pwa'
            );
            return;
        }

        // 2. Richiesta permesso notifiche
        const permission = await Notification.requestPermission();
        runInAction(() => { this.pushNotificationPermission = permission; });

        if (permission !== 'granted') {
            this.notificationAdd(
                langstore.t('push_permission_denied', 'Permesso notifiche negato. Abilitalo dalle impostazioni del browser.'),
                'push_denied'
            );
            return;
        }

        // 3. VAPID key + subscription PushManager + registrazione sul BE
        await performSubscription(this);

        runInAction(() => { this.pushNotificationSubscribed = true; });
        this.notificationAdd(
            langstore.t('push_subscribed', 'Notifiche push attivate'),
            'push_subscribed',
          10000,
          'success'
        );
    } catch (e) {
        this.notificationAdd(
            langstore.t('push_subscribe_error', 'Errore durante l\'attivazione delle notifiche push', e),
            'push_subscribe_error',
          10000,
          'error'
        );
    } finally {
        runInAction(() => { this.pushNotificationLoading = false; });
    }
};

/**
 * Cancella la subscription dal browser e notifica il BE.
 */
export const pushNotificationUnsubscribe = async function ()
{
    if (this.pushNotificationLoading) return;

    runInAction(() => { this.pushNotificationLoading = true; });

    try {
        const subscription = await getNotificationSubscriptions();
        if (subscription) {
            // Notifica il BE prima di cancellare (finché abbiamo ancora l'endpoint)
            await this.dataLayer({
                url: this.config.paths.apiURL + 'push/unsubscribe',
                cacheAge: null,
                params: {
                    endpoint:   subscription.endpoint
                },
                userToken: this.userToken
            });

            await subscription.unsubscribe();
        }

        runInAction(() => { this.pushNotificationSubscribed = false; });
        this.notificationAdd(
            langstore.t('push_unsubscribed', 'Notifiche push disattivate'),
            'push_unsubscribed',
          10000,
          'success'
        );
    } catch (e) {
        this.notificationAdd(
            langstore.t('push_unsubscribe_error', 'Errore durante la disattivazione delle notifiche push'),
            'push_unsubscribe_error',
          10000,
          'error'
        );
    } finally {
        runInAction(() => { this.pushNotificationLoading = false; });
    }
};
